import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const PLAN_IDS = ['starter', 'pro'];

const PLAN_PRICES = {
  starter: '10\u20AC',
  pro: '29,99\u20AC',
};

const PLAN_NAMES = { starter: 'Starter', pro: 'Pro' };

function Billing({ token, onAuthError }) {
  const { t } = useTranslation();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(null);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchTenant();

    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    if (checkoutStatus === 'success') {
      setBanner({ type: 'success', text: t('billing.successBanner') });
    } else if (checkoutStatus === 'cancelled') {
      setBanner({ type: 'info', text: t('billing.cancelledBanner') });
    }
    if (checkoutStatus) {
      params.delete('checkout');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTenant = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/tenant`, authHeaders);
      setTenant(response.data.data);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('billing.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    setRedirecting(planId);
    setError('');
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/billing/checkout`,
        { plan_type: planId },
        authHeaders
      );
      window.location.href = response.data.url;
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('billing.checkoutError'));
      setRedirecting(null);
    }
  };

  const handleManageBilling = async () => {
    setRedirecting('portal');
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/v1/billing/portal`, {}, authHeaders);
      window.location.href = response.data.url;
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('billing.portalError'));
      }
      setRedirecting(null);
    }
  };

  const currentPlan = tenant?.plan_type || 'starter';
  const hasSubscription = Boolean(tenant?.stripe_customer_id);

  const planFeatures = {
    starter: [t('landing.planStarterFeature1'), t('landing.planFeatureCommon'), t('landing.planStarterFeature3')],
    pro: [t('landing.planProFeature1'), t('landing.planFeatureCommon'), t('landing.planProFeature3')],
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-white text-2xl font-bold">{t('billing.title')}</h2>
        <p className="text-muted text-sm mt-1">{t('billing.subtitle')}</p>
      </div>

      {banner && (
        <div
          className={`text-sm rounded-lg p-4 mb-6 border ${
            banner.type === 'success'
              ? 'bg-[#0f2a0f] border-[#166534] text-[#4ade80]'
              : 'bg-surfaceAlt border-edge text-muted'
          }`}
        >
          {banner.text}
        </div>
      )}

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-muted text-center py-10">{t('dashboard.loading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6 max-w-2xl">
            {PLAN_IDS.map((planId) => {
              const isCurrent = currentPlan === planId;
              return (
                <div
                  key={planId}
                  className={`bg-surface border rounded-2xl p-6 flex flex-col ${
                    isCurrent ? 'border-accent' : 'border-edge'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white text-lg font-bold">{PLAN_NAMES[planId]}</h3>
                    {isCurrent && (
                      <span className="bg-surfaceAlt text-accent text-xs px-2.5 py-1 rounded-full border border-edge">
                        {t('billing.currentPlanBadge')}
                      </span>
                    )}
                  </div>
                  <p className="text-white text-3xl font-bold tracking-tight mb-4">
                    {PLAN_PRICES[planId]}
                    <span className="text-muted text-sm font-normal">{t('landing.perMonth')}</span>
                  </p>
                  <ul className="flex flex-col gap-2 mb-6 flex-1">
                    {planFeatures[planId].map((f) => (
                      <li key={f} className="text-muted text-sm flex items-start gap-2">
                        <span className="text-accent">{'\u2713'}</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleSelectPlan(planId)}
                    disabled={isCurrent || redirecting !== null}
                    className={`font-bold text-sm px-4 py-2.5 rounded-lg ${
                      isCurrent
                        ? 'bg-surfaceAlt text-muted cursor-not-allowed'
                        : 'bg-accent text-panel cursor-pointer'
                    } ${redirecting && redirecting !== planId ? 'opacity-50' : ''}`}
                  >
                    {isCurrent
                      ? t('billing.yourCurrentPlan')
                      : redirecting === planId
                      ? t('billing.redirecting')
                      : t('billing.changeToPlan', { plan: PLAN_NAMES[planId] })}
                  </button>
                </div>
              );
            })}
          </div>

          {hasSubscription && (
            <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-2xl">
              <p className="text-white text-sm font-semibold mb-1">{t('billing.manageBilling')}</p>
              <p className="text-muted text-xs mb-4">
                {t('billing.manageBillingDesc')}
              </p>
              <button
                onClick={handleManageBilling}
                disabled={redirecting !== null}
                className={`bg-transparent border border-edge rounded-lg px-4 py-2.5 text-white text-sm cursor-pointer ${
                  redirecting ? 'opacity-50' : ''
                }`}
              >
                {redirecting === 'portal' ? t('billing.opening') : t('billing.openPortal')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Billing;